import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import type { StopType } from '#domain/Stop.ts';

import type { StopLoadMetric } from './loadMetrics.ts';

type MetricProps = {
    exceeded?: boolean;
    label: string;
    metric: StopLoadMetric;
    stopType: StopType;
    unit: string;
};

const getDisplayValue = (value: number, unit: string) => {
    const useTonnes = unit === 'kg' && Math.abs(value) > 1000;
    return {
        value: useTonnes ? value / 1000 : value,
        unit: useTonnes ? 't' : unit,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    };
};

export const Metric = ({ exceeded = false, label, metric, stopType, unit }: MetricProps) => {
    const intl = useIntl();
    const total = getDisplayValue(metric.total, unit);
    const delta = getDisplayValue(Math.abs(metric.delta), unit);
    const deviation = metric.deviation ? getDisplayValue(Math.abs(metric.deviation), unit) : undefined;
    const deltaPrefix = stopType === 'unload' ? '−' : '+';
    const deltaClassName = stopType === 'unload' ? 'label-danger' : 'label-success';
    const metricLabel = intl.formatMessage({ id: label });
    const formattedTotalValue = intl.formatNumber(total.value, {
        minimumFractionDigits: total.minimumFractionDigits,
        maximumFractionDigits: total.maximumFractionDigits,
    });
    const totalDescription = intl.formatMessage(
        {
            id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.total.tooltip',
        },
        {
            label: metricLabel,
            value: formattedTotalValue,
            unit: total.unit,
        }
    );
    const capacityDescription = exceeded
        ? intl.formatMessage(
              {
                  id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.capacity.exceeded',
                  defaultMessage: 'Vehicle capacity exceeded: {dimensions}',
              },
              { dimensions: `${metricLabel} ${formattedTotalValue} ${total.unit}` }
          )
        : undefined;
    const totalTooltipDescription = capacityDescription ?? totalDescription;
    const deltaDescription = intl.formatMessage(
        {
            id:
                stopType !== 'unload'
                    ? 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.delta.added'
                    : 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.delta.removed',
        },
        {
            value: intl.formatNumber(delta.value, {
                minimumFractionDigits: delta.minimumFractionDigits,
                maximumFractionDigits: delta.maximumFractionDigits,
            }),
            unit: delta.unit,
        }
    );
    const deviationPrefix = metric.deviation && metric.deviation > 0 ? '+' : '−';
    const deviationDescription = deviation
        ? intl.formatMessage(
              {
                  id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.load-metrics.deviation',
              },
              {
                  value: `${deviationPrefix}${intl.formatNumber(deviation.value, {
                      minimumFractionDigits: deviation.minimumFractionDigits,
                      maximumFractionDigits: deviation.maximumFractionDigits,
                  })}`,
                  unit: deviation.unit,
              }
          )
        : undefined;
    const totalValue = (
        <SimpleTooltip content={totalTooltipDescription}>
            <span
                aria-label={totalTooltipDescription}
                className={`text-size-14 ${exceeded ? 'text-color-danger' : ''} text-medium cursor-help white-space-nowrap`}
                data-testid='load-metric-total'
                role='img'
            >
                <FormattedNumber
                    value={total.value}
                    minimumFractionDigits={total.minimumFractionDigits}
                    maximumFractionDigits={total.maximumFractionDigits}
                />{' '}
                {total.unit}
            </span>
        </SimpleTooltip>
    );

    const changeValues = (
        <>
            {metric.changed && metric.delta !== 0 && (
                <SimpleTooltip content={deltaDescription}>
                    <span
                        aria-label={deltaDescription}
                        className={`label ${deltaClassName} label-condensed cursor-help`}
                        data-testid='load-metric-delta'
                        role='img'
                    >
                        {deltaPrefix}
                        <FormattedNumber
                            value={delta.value}
                            minimumFractionDigits={delta.minimumFractionDigits}
                            maximumFractionDigits={delta.maximumFractionDigits}
                        />{' '}
                        {delta.unit}
                    </span>
                </SimpleTooltip>
            )}
            {deviation && deviationDescription && (
                <SimpleTooltip content={deviationDescription}>
                    <span
                        aria-label={deviationDescription}
                        className='label label-warning label-condensed cursor-help'
                        data-testid='load-metric-deviation'
                        role='img'
                    >
                        {deviationPrefix}
                        <FormattedNumber
                            value={deviation.value}
                            minimumFractionDigits={deviation.minimumFractionDigits}
                            maximumFractionDigits={deviation.maximumFractionDigits}
                        />{' '}
                        {deviation.unit}
                    </span>
                </SimpleTooltip>
            )}
        </>
    );

    return (
        <div className='display-flex min-width-0 flex-0'>
            <div className='display-flex flex-column align-items-start' data-testid='load-metric-content'>
                <span className='text-size-12 text-color-darker'>
                    <FormattedMessage id={label} />
                </span>
                <div
                    className='display-flex align-items-center flex-wrap gap-5 white-space-nowrap'
                    data-testid='load-metric-values'
                >
                    {totalValue}
                    {changeValues}
                </div>
            </div>
        </div>
    );
};
